import axiosInstance from '../services/api/axiosInstance';
import { getOrCreateVisitorSessionKey } from './visitorSession';
import { CREATOR_ATTRIBUTION_SOURCE } from './creatorAttributionConstants';
import { trackPlatformEvent } from './platformAnalytics';

const REFERRAL_COOKIE = 'qumashi_ref_attribution';
const REFERRAL_STORAGE_KEY = 'qumashi-ref-attribution';
const CLICK_DEDUPE_KEY = 'qumashi-ref-click-dedupe';
const CLICK_DEDUPE_MS = 15 * 60 * 1000;

/** Coalesce parallel track calls (React Strict Mode remounts, duplicate effects). */
const inFlightClickTracks = new Map<string, Promise<ReferralAttributionSession | null>>();

export type ReferralAttributionSession = {
  code: string;
  sessionId: string;
  attributionId?: string;
  attributionSessionId?: string;
  expiresAt?: string;
  creatorProfileId?: string;
  creatorSlug?: string;
  sourceType?: string;
  /** Present when buyer arrived via campaign link (?cmp= or campaign referral link). */
  campaignId?: string;
  referralLinkId?: string;
};

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, expiresAt?: string): void {
  if (typeof document === 'undefined') return;
  const expires = expiresAt
    ? `; expires=${new Date(expiresAt).toUTCString()}`
    : '; max-age=1209600';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax${expires}`;
}

function parseSession(raw: string | null): ReferralAttributionSession | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ReferralAttributionSession;
    if (!parsed.code || !parsed.sessionId) return null;
    if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() < Date.now()) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getReferralAttributionSession(): ReferralAttributionSession | null {
  const fromCookie = parseSession(readCookie(REFERRAL_COOKIE));
  if (fromCookie) return fromCookie;
  try {
    return parseSession(globalThis.localStorage?.getItem(REFERRAL_STORAGE_KEY) ?? null);
  } catch {
    return null;
  }
}

export function setReferralAttributionSession(session: ReferralAttributionSession): void {
  const payload = JSON.stringify(session);
  writeCookie(REFERRAL_COOKIE, payload, session.expiresAt);
  try {
    globalThis.localStorage?.setItem(REFERRAL_STORAGE_KEY, payload);
  } catch {
    // Non-blocking
  }
}

export function clearReferralAttributionSession(): void {
  if (typeof document !== 'undefined') {
    document.cookie = `${REFERRAL_COOKIE}=; path=/; max-age=0`;
  }
  try {
    globalThis.localStorage?.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // Non-blocking
  }
}

function deviceType(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipad/.test(ua)) return 'mobile';
  if (/tablet/.test(ua)) return 'tablet';
  return 'desktop';
}

export function normalizeReferralLandingPath(path?: string): string {
  if (!path?.trim()) return globalThis.location?.pathname ?? '/';
  const trimmed = path.trim();
  const q = trimmed.indexOf('?');
  return (q >= 0 ? trimmed.slice(0, q) : trimmed) || '/';
}

function clickTrackKey(key: string, landingPath: string): string {
  return `${key}:${landingPath}`;
}

function markClickTracked(key: string, landingPath: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(CLICK_DEDUPE_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    map[clickTrackKey(key, landingPath)] = Date.now();
    sessionStorage.setItem(CLICK_DEDUPE_KEY, JSON.stringify(map));
  } catch {
    // Non-blocking
  }
}

function isRecentlyTracked(key: string, landingPath: string): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    const raw = sessionStorage.getItem(CLICK_DEDUPE_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    const last = map[clickTrackKey(key, landingPath)];
    return Boolean(last && Date.now() - last < CLICK_DEDUPE_MS);
  } catch {
    return false;
  }
}

export function extractRefCode(searchParams: URLSearchParams): string | null {
  const ref = searchParams.get('ref')?.trim();
  return ref ? ref.toUpperCase() : null;
}

type TrackResponse = {
  sessionId: string;
  code: string;
  creatorProfileId: string;
  creatorSlug?: string;
  sourceType?: string;
  attributionSessionId?: string;
  attributionId?: string;
  expiresAt: string;
  referralLinkId?: string | null;
  campaignId?: string | null;
};

async function postTrack(payload: Record<string, unknown>): Promise<ReferralAttributionSession | null> {
  try {
    const { data } = await axiosInstance.post<TrackResponse>('/public/referral/track', payload);
    const session: ReferralAttributionSession = {
      code: data.code,
      sessionId: data.sessionId,
      attributionId: data.attributionSessionId ?? data.attributionId,
      attributionSessionId: data.attributionSessionId,
      expiresAt: data.expiresAt,
      creatorProfileId: data.creatorProfileId,
      creatorSlug: data.creatorSlug,
      sourceType: data.sourceType,
      campaignId: data.campaignId ?? undefined,
      referralLinkId: data.referralLinkId ?? undefined,
    };
    setReferralAttributionSession(session);
    trackPlatformEvent({
      eventType: 'REFERRAL_CLICK',
      creatorProfileId: data.creatorProfileId,
      channel: 'referral',
      metadata: { code: data.code, sourceType: data.sourceType },
    });
    return session;
  } catch {
    return null;
  }
}

export async function trackReferralClick(opts: {
  code: string;
  landingPath?: string;
  referralLinkId?: string;
  campaignId?: string;
}): Promise<ReferralAttributionSession | null> {
  const code = opts.code.trim().toUpperCase();
  if (!code) return null;

  const landingPath = normalizeReferralLandingPath(opts.landingPath);
  const dedupeKey = clickTrackKey(code, landingPath);

  const pending = inFlightClickTracks.get(dedupeKey);
  if (pending) return pending;

  if (isRecentlyTracked(code, landingPath)) {
    const existing = getReferralAttributionSession();
    if (existing?.code === code) return existing;
    return null;
  }

  markClickTracked(code, landingPath);

  const promise = (async (): Promise<ReferralAttributionSession | null> => {
    const existing = getReferralAttributionSession();
    const sessionId = existing?.sessionId ?? getOrCreateVisitorSessionKey();

    return postTrack({
      code,
      sessionId,
      landingPath,
      referralLinkId: opts.referralLinkId,
      campaignId: opts.campaignId,
      deviceType: deviceType(),
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    });
  })();

  inFlightClickTracks.set(dedupeKey, promise);
  try {
    return await promise;
  } finally {
    inFlightClickTracks.delete(dedupeKey);
  }
}

/** Storefront slug visit — attributes creator without ?ref=. */
export async function trackStorefrontSlugReferral(
  creatorSlug: string,
  landingPath: string,
): Promise<ReferralAttributionSession | null> {
  const slug = creatorSlug.trim().toLowerCase();
  if (!slug) return null;

  const normalized = normalizeReferralLandingPath(landingPath);
  const dedupeKey = clickTrackKey(`slug:${slug}`, normalized);

  const pending = inFlightClickTracks.get(dedupeKey);
  if (pending) return pending;

  if (isRecentlyTracked(`slug:${slug}`, normalized)) {
    const existing = getReferralAttributionSession();
    if (existing?.creatorSlug === slug) return existing;
    return null;
  }

  markClickTracked(`slug:${slug}`, normalized);

  const promise = (async (): Promise<ReferralAttributionSession | null> => {
    const existing = getReferralAttributionSession();
    const sessionId = existing?.sessionId ?? getOrCreateVisitorSessionKey();

    return postTrack({
      creatorSlug: slug,
      sessionId,
      landingPath: normalized,
      deviceType: deviceType(),
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    });
  })();

  inFlightClickTracks.set(dedupeKey, promise);
  try {
    return await promise;
  } finally {
    inFlightClickTracks.delete(dedupeKey);
  }
}

/** Legacy alias — implicit storefront attribution via resolved code. */
export async function trackImplicitStorefrontReferral(
  code: string,
  landingPath: string,
): Promise<ReferralAttributionSession | null> {
  return trackReferralClick({ code: code.trim().toUpperCase(), landingPath });
}

export async function handleReferralFromSearchParams(
  searchParams: URLSearchParams,
): Promise<ReferralAttributionSession | null> {
  const code = extractRefCode(searchParams);
  if (!code) return getReferralAttributionSession();

  const linkId = searchParams.get('lid')?.trim() || undefined;
  const campaignId = searchParams.get('cmp')?.trim() || undefined;
  return trackReferralClick({
    code,
    landingPath: globalThis.location?.pathname,
    referralLinkId: linkId,
    campaignId,
  });
}

/** After successful order — avoid stale re-attribution on next purchase. */
export function consumeReferralAttributionAfterOrder(): void {
  clearReferralAttributionSession();
}

type ValidateSessionResponse =
  | {
      valid: true;
      code: string;
      sessionId: string;
      creatorProfileId: string;
      creatorSlug: string | null;
      displayName: string | null;
      expiresAt: string;
      sourceType: string;
      campaignId: string | null;
    }
  | {
      valid: false;
      reason: string;
    };

/** Reconcile browser storage with live server session; clears stale attribution. */
export async function refreshReferralAttributionSession(): Promise<ReferralAttributionSession | null> {
  const stored = getReferralAttributionSession();
  if (!stored?.sessionId) {
    if (stored) clearReferralAttributionSession();
    return null;
  }

  try {
    const { data } = await axiosInstance.post<ValidateSessionResponse>(
      '/public/referral/validate-session',
      { sessionId: stored.sessionId },
    );
    if (!data.valid) {
      clearReferralAttributionSession();
      return null;
    }

    const session: ReferralAttributionSession = {
      code: data.code,
      sessionId: data.sessionId,
      expiresAt: data.expiresAt,
      creatorProfileId: data.creatorProfileId,
      creatorSlug: data.creatorSlug ?? undefined,
      sourceType: data.sourceType,
      campaignId: data.campaignId ?? undefined,
    };
    setReferralAttributionSession(session);
    return session;
  } catch {
    clearReferralAttributionSession();
    return null;
  }
}

export { CREATOR_ATTRIBUTION_SOURCE };
